import { useEffect, useRef, useState, useCallback } from 'react'
import NumberFormat from 'react-number-format'
import { Spinner } from 'reactstrap'

import { useField } from '@unform/core'

import { Container, MessageError } from './styles'

/**
 *
 * @example
 * <InputNumber
 *   name="quantity"
 *   placeholder="Insira o telefone"
 *   loading={loading}
 * />
 *
 * @param {string} name Recebe o name para o input
 * @param {ReactElement | string} icon Ícone que será exbido a esquerda do input.
 * @param {boolean} loading Exibe um loader ao lado do input
 * @param {function} onBlur Extendendo onBlur
 *
 * @returns O valor retornado é um objeto com value(formatado) e numAsString(sem formatação)
 *
 */

type InputNumberProps = {
  name: string
  icon?: string | JSX.Element
  loading?: boolean
  disabled?: boolean
  onBlur?: () => void
}

const InputNumber = ({
  name,
  icon: Icon,
  onBlur,
  loading,
  disabled,
  ...rest
}: InputNumberProps) => {
  const inputRef = useRef(null)

  const { fieldName, defaultValue, error, registerField, clearError } =
    useField(name)

  const [valueInput, setValueInput] = useState('')

  const handleInputBlur = useCallback(() => {
    if (onBlur) {
      onBlur({
        value: inputRef.current.state.value,
        name: inputRef.current.state.name,
        clearError
      })
    }
  }, [clearError, onBlur])

  const defaultValueIsNull = defaultValue === null

  const defaultValueIsUndefined = typeof defaultValue === 'undefined'

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'state'
    })
  }, [inputRef, fieldName, registerField])

  useEffect(() => {
    if (!defaultValueIsNull && !defaultValueIsUndefined) {
      setValueInput(defaultValue)
    }
  }, [defaultValue, defaultValueIsNull, defaultValueIsUndefined])

  const inputChangedHandler = values => {
    setValueInput(values.value)
  }

  return (
    <>
      <Container isErrored={!!error} isDisabled={disabled}>
        {Icon && <Icon />}

        <NumberFormat
          type="text"
          onBlur={handleInputBlur}
          name={fieldName}
          aria-label={fieldName}
          onValueChange={inputChangedHandler}
          allowedDecimalSeparators={[',', '.']}
          value={valueInput}
          defaultValue={defaultValue === null ? '' : defaultValue}
          disabled={disabled}
          {...rest}
          ref={inputRef}
        />

        {loading && (
          <Spinner
            className="rightElement"
            type="grow"
            size="sm"
            color="secondary"
          />
        )}
      </Container>
      {error && <MessageError>{error}</MessageError>}
    </>
  )
}

export default InputNumber

InputNumber.defaultProps = {
  icon: null,
  onBlur: null,
  loading: false,
  disabled: false
}
